import * as React from 'react';
import * as _ from 'lodash';
import { toast } from 'react-toastify';
import { connect, Dispatch } from 'react-redux';
import { Col, Row, FormGroup, FormControl, Button, Label } from 'react-bootstrap';
import { IApplicationState, AppActionCreators, OrdersActionCreators, DashActionCreators, ILookupLabAddressResponse, IAddressValidationResponse } from '@nkr/store/index';
import { IBaseResponse, IHomeBloodDrawRequest, IListItem } from '@nkr/models/index';
import { IUsPsAddressResponse } from '@nkr/models/usps-address-response';
import { OrganType } from '@nkr/enums/index';
import { IBaseDispatchProps, PHLEBOT_REQUEST_FAILED_MESSAGE } from '../models';
import AddressValidation, { IAddress } from '@nkr/components/address-validation';
import Tubes from './tubes';
import ShipTo from './ship-to';
import ReturnTo from './return-to';

interface IProps {
    centerId: number,
    zipCode: string
}

interface IStateProps {
    siteOrganType: OrganType
}

interface IDispatchProps extends IBaseDispatchProps {
    requestHomeBloodDraw: (request: IHomeBloodDrawRequest, centerId: number) => Promise<IBaseResponse>,
    lookUpLabAddress: (centerId: number) => Promise<ILookupLabAddressResponse>,
    validateAddress: (address: string, address2: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    [index: string]: string | boolean | number | IListItem[] | IUsPsAddressResponse | IAddress | null,
    organType: OrganType | null,
    firstName: string,
    lastName: string,
    address1: string,
    address2: string,
    city: string,
    state: string,
    postalCode: string,
    contactPhone: string,
    email: string,
    dob: string,
    billingRef: string,

    returnName: string,
    returnAddress1: string,
    returnAddress2: string,
    returnCity: string,
    returnState: string,
    returnPostalCode: string,
    returnContactPhone: string,

    lav10ml: number,
    pink10ml: number,
    red10ml: number,
    green10ml: number,
    yellow85ml: number,
    blue45ml: number,
    isPhlebot: boolean,
    disableElements: boolean,
    tooManyTubes: boolean,
    stateList: IListItem[],

    showAddressValidation: boolean,
    addressValidationResponse: IUsPsAddressResponse | null,
    submittedAddress: IAddress | null
}

class HomeBloodDrawOrder extends React.Component<TProps, IState> {
    readonly state: IState = {
        organType: this.props.siteOrganType == OrganType.Liver ? OrganType.Liver : null,
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        contactPhone: '',
        email: '',
        dob: '',
        billingRef: '',

        returnName: '',
        returnAddress1: '',
        returnAddress2: '',
        returnCity: '',
        returnState: '',
        returnPostalCode: '',
        returnContactPhone: '',

        lav10ml: 0,
        pink10ml: 0,
        red10ml: 0,
        green10ml: 0,
        yellow85ml: 0,
        blue45ml: 0,
        isPhlebot: false,
        disableElements: false,

        tooManyTubes: false,
        stateList: [],

        showAddressValidation: false,
        addressValidationResponse: null,
        submittedAddress: null
    };

    componentDidMount = () => {
        this.getData();
    };

    getTubeKeys = () => {
        let { lav10ml, pink10ml, red10ml, green10ml, yellow85ml, blue45ml } = this.state;

        let obj = {
            lav10ml,
            pink10ml,
            red10ml,
            green10ml,
            yellow85ml,
            blue45ml
        };

        return obj
    }

    checkLabCountIsOkay = (name: string, val: number): void => {
        let tubeKeys = this.getTubeKeys();

        let checkObj = {
            ...tubeKeys,
            [name]: val
        };

        let count = 0;

        Object.keys(checkObj).forEach(x => count += checkObj[x]);

        // if (count > 8) {
        //     alert('You cannot select more than a total of 8 tubes. Please adjust your order before continuing.');
        // }

        this.setState({
            [name]: val,
            tooManyTubes: count > 8
        });
    }

    tubeCountBiggerThanZero = () => {
        let tubeKeys = this.getTubeKeys();

        let count = 0;

        Object.keys(tubeKeys).forEach(x => count += tubeKeys[x]);

        return count <= 8 && count > 0;
    }

    getData = () => {
        let { zipCode, centerId } = this.props;

        if (zipCode == undefined) {
            zipCode = '';
        }

        this.props.showLoading('Getting Test data, one moment please...').then(() => {
            Promise.all([
                this.props.lookUpZipCode(zipCode),
                this.props.lookUpLabAddress(centerId)
            ])
                .then(([zipData, addressData]) => {
                    let city = '';
                    let state = '';
                    let postalCode = zipCode;
                    let stateList: IListItem[] = [];

                    let returnName = '';
                    let returnAddress1 = '';
                    let returnAddress2 = '';
                    let returnCity = '';
                    let returnState = '';
                    let returnPostalCode = '';

                    if (zipData.data.success) {
                        city = zipData.data.results.city;
                        state = zipData.data.results.state;
                        stateList = zipData.data.results.listOfStates;
                    } else {
                        toast.error(zipData.data.message);
                    }

                    if (addressData.data.success) {
                        returnName = addressData.data.results.returnName;
                        returnAddress1 = addressData.data.results.returnAddress1;
                        returnAddress2 = addressData.data.results.returnAddress2;
                        returnCity = addressData.data.results.returnCity;
                        returnState = addressData.data.results.returnState;
                        returnPostalCode = addressData.data.results.returnPostalCode;
                    } else {
                        toast.error(addressData.data.message);
                    }

                    this.setState({
                        stateList,
                        postalCode,
                        city,
                        state,
                        returnName,
                        returnAddress1,
                        returnAddress2,
                        returnCity,
                        returnState,
                        returnPostalCode
                    });
                })
                .catch((err: any) => {
                    toast.error('error loading data');
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    };

    handleOrganTypeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget.value;

        this.setState({
            organType: value === '' ? null : parseInt(value)
        });
    }

    isFormValid = () => {
        let isValid = this.state.organType != null
            && this.state.firstName
            && this.state.lastName
            && this.state.address1.length > 5
            && this.state.city.length > 2
            && this.state.state.length == 2
            && this.state.postalCode.length == 5
            && this.state.contactPhone.length > 9
            && this.state.email.length > 5
            && this.state.dob.length >= 8
            && this.state.returnName.length > 5
            && this.state.returnAddress1.length > 5
            && this.state.returnCity.length > 2
            && this.state.returnPostalCode.length == 5
            && this.state.returnState.length == 2
            && this.state.billingRef.length > 2
            && this.tubeCountBiggerThanZero();

        return isValid;
    }

    handleTubesChange = (name: string, value: number) => {
        this.checkLabCountIsOkay(name, value);
    }

    handleShipToChange = (name: string, value: string) => {
        this.setState({
            [name]: value
        });
    }

    handleReturnToChange = (name: string, value: string) => {
        this.setState({
            [name]: value
        });
    }

    handleBillingReferenceChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;

        this.setState({
            [name]: value
        });
    }

    handleRequest = () => {
        this.props.showLoading();

        const emailFormat = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(this.state.email);

        if (!emailFormat) {
            this.props.hideLoading();
            return toast.error('Email is not in the proper format.');
        }

        let { address1, address2, city, state, postalCode } = this.state;

        this.props.validateAddress(address1, address2, city, state, postalCode)
            .then(response => {
                if (response.data.success) {
                    this.placeOrder();

                    return;
                }

                this.props.hideLoading();

                this.setState({
                    showAddressValidation: true,
                    addressValidationResponse: response.data.results,
                    submittedAddress: {
                        street: address1,
                        street2: address2,
                        city,
                        state,
                        postalCode
                    }
                });
            });
    }

    handleUseAddress = (recommended: boolean) => {
        let response = this.state.addressValidationResponse;

        let updates: Partial<IState> = recommended
            ? {
                address1: response.address1,
                address2: response.address2,
                city: response.city,
                state: response.state,
                postalCode: response.zip5
            }
            : {};

        this.setState({
            ...updates,
            showAddressValidation: false,
            addressValidationResponse: null,
            submittedAddress: null
        } as IState, () => {
            this.props.showLoading();
            this.placeOrder();
        });
    }

    handleCancelAddress = () => {
        this.setState({
            showAddressValidation: false,
            addressValidationResponse: null,
            submittedAddress: null
        });
    }

    placeOrder = () => {
        let { showAddressValidation, addressValidationResponse, submittedAddress, ...hbdRequest } = this.state;

        this.props.requestHomeBloodDraw(hbdRequest as IHomeBloodDrawRequest, this.props.centerId)
            .then(response => {
                if (response.data.success) {

                    this.setState({
                        disableElements: true
                    });
                    
                    const redirect = () => {
                        window.location.href = `/center/${this.props.centerId}/action-center/home-blood-draw`;
                    };

                    if (response.data.results?.phlebotRequestFailed) {
                        toast.warning(PHLEBOT_REQUEST_FAILED_MESSAGE, {
                            autoClose: 10000,
                            onClose: redirect
                        });
                    } else {
                        toast.success('Request has been completed.', {
                            autoClose: 2000,
                            onClose: redirect
                        });
                    }
                } else {
                    toast.error('Unable to complete request: ' + response.data.message);
                }
            })
            .finally(() => {
                this.props.hideLoading();
            });
    }

    render = () => {
        let isValid = this.isFormValid();
        let isLiverSite = this.props.siteOrganType == OrganType.Liver;
        let organTypeDisabled = this.state.disableElements || isLiverSite;

        return (
            <div className='container' style={{ paddingBottom: 20 }}>
                <Row>
                    <Col md={12}>
                        <h3>Request Home Blood Draw</h3>
                    </Col>
                </Row>

                <form>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <label>Organ Type:<span style={{ color: 'red' }}>*</span></label>
                                <FormControl
                                    name='organType'
                                    componentClass='select'
                                    value={this.state.organType == null ? '' : this.state.organType}
                                    onChange={this.handleOrganTypeChange}
                                    disabled={organTypeDisabled}
                                >
                                    <option value={''}>- Please Select -</option>
                                    <option value={OrganType.Kidney}>Kidney</option>
                                    <option value={OrganType.Liver}>Liver</option>
                                </FormControl>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Tubes
                                lav10ml={this.state.lav10ml}
                                pink10ml={this.state.pink10ml}
                                red10ml={this.state.red10ml}
                                green10ml={this.state.green10ml}
                                yellow85ml={this.state.yellow85ml}
                                blue45ml={this.state.blue45ml}
                                disableElements={this.state.disableElements}
                                onFieldChanged={this.handleTubesChange}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <ShipTo
                                firstName={this.state.firstName}
                                lastName={this.state.lastName}
                                address1={this.state.address1}
                                address2={this.state.address2}
                                city={this.state.city}
                                state={this.state.state}
                                postalCode={this.state.postalCode}
                                contactPhone={this.state.contactPhone}
                                email={this.state.email}
                                dob={this.state.dob}
                                disableElements={this.state.disableElements}
                                stateList={this.state.stateList}
                                onFieldChange={this.handleShipToChange}
                            />
                        </Col>

                        <Col md={6}>
                            <ReturnTo
                                centerId={this.props.centerId}
                                returnName={this.state.returnName}
                                returnAddress1={this.state.returnAddress1}
                                returnAddress2={this.state.returnAddress2}
                                returnCity={this.state.returnCity}
                                returnState={this.state.returnState}
                                returnPostalCode={this.state.returnPostalCode}
                                returnContactPhone={this.state.returnContactPhone}
                                disableElements={this.state.disableElements}
                                onFieldChange={this.handleReturnToChange}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <label>Billing Reference:<span style={{ color: 'red' }}>*</span></label>
                                <FormControl
                                    name='billingRef'
                                    value={this.state.billingRef}
                                    disabled={this.state.disableElements}
                                    onChange={this.handleBillingReferenceChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <div className='text-center' style={{ marginBottom: 15 }}>
                        <Label bsStyle='info'>Please avoid PHI. This is what you'll see on your invoice to identify this charge.</Label>
                    </div>

                    <div className='text-center'>
                        <Button bsStyle='primary' onClick={this.handleRequest} disabled={!isValid || this.state.disableElements}>Order Kit and Home Blood Draw</Button>
                    </div>
                </form>

                <AddressValidation
                    show={this.state.showAddressValidation}
                    response={this.state.addressValidationResponse}
                    submittedAddresss={this.state.submittedAddress}
                    onUseAddress={this.handleUseAddress}
                    onCancel={this.handleCancelAddress}
                />
            </div>
        );
    };
}


const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        requestHomeBloodDraw: (request, centerId) => {
            return dispatch(OrdersActionCreators.requestHomeBloodDraw(request, centerId))
        },
        lookUpLabAddress: (centerId) => {
            return dispatch(OrdersActionCreators.lookUpLabAddress(centerId))
        },
        validateAddress: (address, address2, city, state, postalCode) => {
            return dispatch(DashActionCreators.validateAddress(address, address2, city, state, postalCode));
        },
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        lookUpZipCode: (code) => {
            return dispatch(OrdersActionCreators.lookUpZipCode(code))
        }
    };
};

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        siteOrganType: appState.app.site.organType
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(HomeBloodDrawOrder);
